import React from "react";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import {
  formatteDateDay,
  generateUniqueMatterName,
  pageScrollStyle,
} from "../../../styles/styles";
import { generateInvoiceNumber } from "../../../pages/LawyerPages/Billing/BillingPage";
import { getCurrencySymbol } from "../../../pages/LawyerPages/Settings/CurrencySetForm";

const EditBillPreview = ({
  editBillData,
  values,
  changeClientAddress,
  removedRowIds = [],
  currency = "USD", // Added currency prop with default value
}) => {
  const theme = useTheme();

  const currencySymbol = getCurrencySymbol(currency) || "$";

  const entryData = editBillData
    ? {
        invoice_num: generateInvoiceNumber(editBillData.id),
        full_data: editBillData,
      }
    : { invoice_num: values.invoice_num, full_data: { expenses: [] } };

  const billableTotal =
    entryData.full_data.expenses
      ?.filter((e) => !e.non_billable)
      .reduce((sum, e) => sum + (parseFloat(e.total_payment) || 0), 0)
      ?.toFixed(2) || "0.00";

  const nonBillableTotal =
    entryData.full_data.expenses
      ?.filter((e) => e.non_billable)
      .reduce((sum, e) => sum + (parseFloat(e.total_payment) || 0), 0)
      ?.toFixed(2) || "0.00";

  const InvoiceData = {
    firmData: {
      name:
        values.firm_name || entryData.full_data.firm?.name || "Your Firm Name",
      address:
        values.firm_address ||
        entryData.full_data.firm?.address ||
        "Firm Address",
      city: values.firm_city || entryData.full_data.firm?.city || "City",
      state: values.firm_state || entryData.full_data.firm?.state || "State",
      zip:
        values.firm_pincode || entryData.full_data.firm?.pin_code || "Pin Code",
    },
    clientData: {
      name: (() => {
        if (values.client) {
          if (typeof values.client === "object") {
            if (values.client.company_name) return values.client.company_name;
            return `${values.client.first_name || ""} ${
              values.client.last_name || ""
            }`.trim();
          }
          if (entryData.full_data.case?.case_related_person) {
            return `${
              entryData.full_data.case.case_related_person.first_name || ""
            } ${
              entryData.full_data.case.case_related_person.last_name || ""
            }`.trim();
          }
          return values.client;
        }
        if (entryData.full_data.case?.case_related_person) {
          return `${
            entryData.full_data.case.case_related_person.first_name || ""
          } ${
            entryData.full_data.case.case_related_person.last_name || ""
          }`.trim();
        }
        return "Client Name";
      })(),
      address: changeClientAddress
        ? values.client_address
        : entryData.full_data.case?.case_related_person?.address_1,
      city: changeClientAddress
        ? values.client_city
        : entryData.full_data.case?.case_related_person?.city,
      state: changeClientAddress
        ? values.client_state
        : entryData.full_data.case?.case_related_person?.state,
      zip: changeClientAddress
        ? values.client_pincode
        : entryData.full_data.case?.case_related_person?.pincode,
      case: values.case
        ? generateUniqueMatterName(
            values.case,
            entryData.full_data.case?.case_name || ""
          )
        : entryData.full_data.case?.id
        ? generateUniqueMatterName(
            entryData.full_data.case.id,
            entryData.full_data.case.case_name
          )
        : "--",
    },
    billData: {
      invoice_num: values.invoice_num || entryData.invoice_num,
      issue_date: values.issue_date
        ? dayjs(values.issue_date).format("MM/DD/YYYY")
        : formatteDateDay(entryData.full_data.issue_date),
      due_date: values.due_date
        ? dayjs(values.due_date).format("MM/DD/YYYY")
        : formatteDateDay(entryData.full_data.due_date),
      status: values.status || entryData.full_data.status || "draft",
      total_amount: billableTotal, // Added total amount for consistency
    },
    InvoiceAccountRow: [
      {
        invoice_num: values.invoice_num || entryData.invoice_num,
        due_date: values.due_date
          ? dayjs(values.due_date).format("MM/DD/YYYY")
          : formatteDateDay(entryData.full_data.due_date),
        amount_due: billableTotal,
        payment_get: "0.00",
        balance_due: billableTotal,
      },
    ],
  };

  const allRows =
    entryData.full_data.expenses
      ?.filter((exp) => !exp.non_billable || exp.bill_entry_show)
      .map((exp, index) => {
        const id = exp.id || `temp_${index}`;
        return {
          id,
          type: exp.type || "--",
          due_date: formatteDateDay(entryData.full_data.due_date),
          quantity: exp.quantity || 0,
          rate: exp.hourly_rate || "0.00",
          non_billable_amount: exp.non_billable ? exp.total_payment : "0.00",
          billable_amount: !exp.non_billable ? exp.total_payment : "0.00",
          removed: removedRowIds.includes(id),
        };
      }) || [];

  // Updated columns to include currency symbol
  const columns = [
    { field: "type", headerName: "Type", width: "16%" },
    { field: "due_date", headerName: "Due Date", width: "16%" },
    { field: "quantity", headerName: "Qty", width: "16%" },
    { field: "rate", headerName: `Rate(${currencySymbol})`, width: "16%" },
    {
      field: "non_billable_amount",
      headerName: `Non-billable(${currencySymbol})`,
      width: "18%",
    },
    {
      field: "billable_amount",
      headerName: "Total Amount",
      width: "18%",
      align: "right",
    },
  ];

  const InvoiceAccount = [
    { field: "invoice_num", headerName: "Invoice Number", width: "20%" },
    { field: "due_date", headerName: "Due Date", width: "20%" },
    { field: "amount_due", headerName: "Amount Due", width: "20%" },
    { field: "payment_get", headerName: "Payment Received", width: "20%" },
    {
      field: "balance_due",
      headerName: "Balance Due",
      width: "20%",
      align: "right",
    },
  ];

  const renderSummaryTable = () => (
    <TableContainer sx={{ pageBreakInside: "avoid" }}>
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow sx={{ pageBreakInside: "avoid" }}>
            {columns.map((col) => (
              <TableCell
                key={col.field}
                sx={{
                  fontWeight: 550,
                  fontSize: "0.8rem",
                  width: col.width,
                  padding: 1,
                  textAlign: col.align || "left",
                  backgroundColor: theme.palette.grey[200],
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
                align={col.align || "left"}
              >
                {col.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {allRows.length > 0 ? (
            allRows.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  backgroundColor: row.removed
                    ? theme.palette.error.light
                    : "inherit",
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    sx={{
                      fontSize: "0.7rem",
                      padding: 1,
                      width: col.width,
                      textAlign: col.align || "left",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                    align={col.align || "left"}
                  >
                    {col.field === "non_billable_amount" ||
                    col.field === "billable_amount" ||
                    col.field === "rate"
                      ? `${currencySymbol}${row[col.field] ?? "0.00"}`
                      : row[col.field] ?? "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                align="center"
                sx={{ fontSize: "0.75rem", padding: 2 }}
              >
                No Data Available
              </TableCell>
            </TableRow>
          )}
          {allRows.length > 0 && (
            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
              <TableCell
                colSpan={4}
                align="right"
                sx={{ fontWeight: 600, padding: 1 }}
              >
                Total:
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: 600, padding: 1 }}>
                {currencySymbol}
                {nonBillableTotal}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, padding: 1 }}>
                {currencySymbol}
                {billableTotal}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderAccountTable = () => (
    <TableContainer sx={{ pageBreakInside: "avoid" }}>
      <Table
        sx={{
          tableLayout: "fixed",
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
      >
        <TableHead>
          <TableRow sx={{ pageBreakInside: "avoid" }}>
            {InvoiceAccount.map((col) => (
              <TableCell
                key={col.field}
                sx={{
                  fontWeight: 550,
                  fontSize: "0.8rem",
                  width: col.width,
                  padding: 1,
                  textAlign: col.align || "left",
                  backgroundColor: theme.palette.grey[200],
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
                align={col.align || "left"}
              >
                {col.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {InvoiceData.InvoiceAccountRow.map((invoice, index) => (
            <TableRow key={index}>
              {InvoiceAccount.map((col) => (
                <TableCell
                  key={col.field}
                  sx={{
                    fontSize: "0.7rem",
                    padding: 1,
                    width: col.width,
                    textAlign: col.align || "left",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                  align={col.align || "left"}
                >
                  {col.field === "amount_due" ||
                  col.field === "balance_due" ||
                  col.field === "payment_get"
                    ? `${currencySymbol}${parseFloat(
                        invoice[col.field] || 0
                      ).toFixed(2)}`
                    : invoice[col.field] ?? "-"}
                </TableCell>
              ))}
            </TableRow>
          ))}
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            <TableCell
              colSpan={InvoiceAccount.length - 1}
              align="right"
              sx={{ fontWeight: 600, padding: 1 }}
            >
              Outstanding Balance:
            </TableCell>
            <TableCell sx={{ fontWeight: 600, padding: 1, textAlign: "right" }}>
              {currencySymbol}
              {parseFloat(
                InvoiceData?.InvoiceAccountRow[0]?.balance_due || 0
              ).toFixed(2)}
            </TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            <TableCell
              colSpan={InvoiceAccount.length - 1}
              align="right"
              sx={{ fontWeight: 600, padding: 1 }}
            >
              Total Amount Outstanding:
            </TableCell>
            <TableCell sx={{ fontWeight: 600, padding: 1, textAlign: "right" }}>
              {currencySymbol}
              {parseFloat(
                InvoiceData?.InvoiceAccountRow[0]?.balance_due || 0
              ).toFixed(2)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Card
      elevation={2}
      sx={{ height: "fit-content", overflow: "auto", ...pageScrollStyle }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            variant="body1"
            sx={{ fontSize: "1.3rem", fontWeight: 500 }}
          >
             {InvoiceData.firmData.name}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
            {InvoiceData.firmData.address}, {InvoiceData.firmData.city},
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
            {InvoiceData.firmData.state}-{InvoiceData.firmData.zip}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: "1.5rem",
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: "50%" }, mb: { xs: 2, sm: 0 } }}>
            <Typography
              variant="body1"
              sx={{ fontSize: "0.9rem", fontWeight: "500" }}
            >
              Bill To:
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: "0.8rem", textTransform: "capitalize" }}
            >
              {InvoiceData.clientData.name}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
              {InvoiceData.clientData.address
                ? `${InvoiceData.clientData.address}, ${InvoiceData.clientData.city}`
                : ""}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
              {InvoiceData.clientData.state || ""}{" "}
              {InvoiceData.clientData.zip || ""}
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: "0.8rem", fontWeight: "500", mt: 1 }}
            >
              Matter: {InvoiceData.clientData.case}
            </Typography>
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "50%" }, textAlign: "right" }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: "500" }}>
              Invoice No: {InvoiceData?.billData?.invoice_num}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: "500" }}>
              Issue Date: {InvoiceData.billData.issue_date}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: "500" }}>
              Due Date: {InvoiceData.billData.due_date}
            </Typography>
            <Typography
              color={
                InvoiceData.billData.status === "draft"
                  ? "info"
                  : InvoiceData.billData.status === "pending"
                  ? "warning"
                  : InvoiceData.billData.status === "unpaid"
                  ? "error"
                  : InvoiceData.billData.status === "paid"
                  ? "success"
                  : "primary"
              }
              sx={{
                fontSize: "0.9rem",
                fontWeight: "500",
                textTransform: "capitalize",
                mt: 1,
              }}
            >
              Bill Status: {InvoiceData.billData.status}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant="body1"
              sx={{ fontSize: "0.9rem", fontWeight: 600, mb: 1 }}
            >
              Summary of the invoice
            </Typography>
            {renderSummaryTable()}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              variant="body1"
              sx={{ fontSize: "0.9rem", fontWeight: 600, mb: 1 }}
            >
              Detailed Statement of Account
            </Typography>
            {renderAccountTable()}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "2rem",
              mt: "2rem",
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                Please pay within 30 days.
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EditBillPreview;
